import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const TONES = {
  primary: "bg-primary/10 text-primary",
  warning: "bg-warning-500/15 text-warning-700",
  success: "bg-success-500/15 text-success-700",
} as const;

/** Collapsible booking card shown inside a conversation (rooms shown, summary, confirmed). */
const BookingCardShell = ({ icon, tone, title, badge, meta, expanded, onToggle, children }: {
  icon: ReactNode; tone: keyof typeof TONES; title: string; badge?: ReactNode; meta?: ReactNode;
  expanded: boolean; onToggle: () => void; children: ReactNode;
}) => (
  <div className="w-full max-w-xl overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-xs">
    <button type="button" aria-expanded={expanded}
      className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-start hover:bg-accent/50"
      onClick={(e) => { e.stopPropagation(); onToggle(); }}>
      <span className={cn("flex size-7 shrink-0 items-center justify-center rounded-md [&_svg]:size-4", TONES[tone])} aria-hidden="true">{icon}</span>
      <span className="text-sm font-semibold">{title}</span>
      {badge && <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", TONES[tone])}>{badge}</span>}
      {meta && <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{meta}</span>}
      <ChevronDown className={cn("ms-auto size-4 shrink-0 text-muted-foreground transition-transform", expanded && "rotate-180")} aria-hidden="true" />
    </button>
    {expanded && <div className="flex flex-col gap-2 border-t border-border px-3 py-3 text-sm">{children}</div>}
  </div>
);

export const DetailRow = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-1">{children}</div>
);

export const Detail = ({ label, children, className }: { label?: string; children: ReactNode; className?: string }) => (
  <span className={className}>
    {label && <span className="text-muted-foreground">{label}: </span>}
    {children}
  </span>
);

export default BookingCardShell;
